import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconTruck,
  IconPackage,
  IconClipboardList,
  IconBox,
  IconClipboardCheck,
  IconTruckDelivery,
  IconShieldCheck,
  IconClock,
} from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { StatCard } from "../../../../components/common/stat-card";
import { Heading } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { PreviewListRow } from "../../../../components/common/preview-list-row";
import { SectionCard } from "../../../../components/common/section-card";
import { dollarFormat } from "../../../../utils/currencies";
import { useMyProducts } from "../../../products/hooks/useMyProducts";
import { usePurchaseOrders } from "../../../purchase-orders/hooks/usePurchaseOrders";
import { usePurchaseOrderStats } from "../../../purchase-orders/hooks/usePurchaseOrderStats";
import { mapPoStatus } from "../../../purchase-orders/utils/po-status";
import { useProductRequests } from "../../../product-requests/hooks/useProductRequests";
import { usePendingRelease, useTopSellerProducts } from "../../hooks/useSellerDashboard";
import { useSupplierWarrantyClaims, useSupplierClaimStats } from "../../../warranty-claims/hooks/useSupplierWarrantyClaims";
import { useMyInbox } from "../../../inbox/hooks/useMyInbox";
import { resolveNotificationLink } from "../../../inbox/resolve-notification-link";
import { SalesQueryDtoStateEnum, type PurchaseOrderDto, type NotificationDto } from "../../../../api";
import catLaptops from "../../../../assets/images/landing/cat-laptops.png";
import { ProductRow } from "../components/product-row";
import { statValue, toTimestamp, formatStatusLabel } from "../components/dashboard-helpers";
import { EmptyRow } from "../components/empty-row";
import { SellerDashboardSkeleton } from "../components/seller-dashboard-skeleton";
import { RecentClaimsSection } from "../components/recent-claims-section";
import { RecentNotificationsSection } from "../components/recent-notifications-section";

// PO states accepted by the backend ('purchase', 'done') aren't all in the
// generated SalesQueryDtoStateEnum, which models sale-order states. Cast via
// the partial query type once the codegen is rerun this can be a typed enum.
const PO_PENDING_STATE = "purchase" as unknown as SalesQueryDtoStateEnum;

const PLACEHOLDER_IMG = catLaptops;

// Phones show the stat cards two-up: stack the icon over the label/value so
// the label keeps its width.
// Phones: tighter row so the amount and "Preview Order" fit on one line.
const PREVIEW_ROW_PHONE =
  "max-md:px-3 max-md:[&>div:first-child]:size-10 max-md:[&_span]:whitespace-nowrap";

const STAT_CARD_PHONE = "max-sm:flex-col max-sm:items-start max-sm:px-4";

export function SellerDashboardPage() {
  const navigate = useNavigate();

  const purchaseOrderStats = usePurchaseOrderStats();
  const myProducts = useMyProducts({ page: 1, pageSize: 1 });
  const pendingRelease = usePendingRelease();
  const claimStats = useSupplierClaimStats();

  const activeProcessingOrders = usePurchaseOrders({ page: 1, pageSize: 10, state: PO_PENDING_STATE });
  const pendingProducts = useProductRequests(
    { page: 1, pageSize: 20 },
    { excludeDrafts: true },
  );
  const topProducts = useTopSellerProducts({ period: "30d", limit: 4 });
  const { data: claimsResp, isLoading: claimsLoading } = useSupplierWarrantyClaims({ pageSize: 3 });
  const { data: inboxResp, isLoading: inboxLoading } = useMyInbox({ pageSize: 3 });

  const recentClaims = (claimsResp?.data ?? []);
  const recentNotifications = (inboxResp?.data ?? []) as NotificationDto[];

  const activeOrders = useMemo(() => {
    const rows = activeProcessingOrders.data?.data ?? [];
    const activeById = new Map<number, PurchaseOrderDto>();

    rows.forEach((order) => {
      const status = mapPoStatus(order);
      if (status === "cancelled") return;
      activeById.set(order.id, order);
    });

    return [...activeById.values()]
      .sort((a, b) => toTimestamp(b.dateOrder) - toTimestamp(a.dateOrder))
      .slice(0, 4);
  }, [activeProcessingOrders.data?.data]);

  const pendingProductRows = (pendingProducts.data?.data ?? [])
    .filter((p) => (p as { status?: string }).status === undefined || (p as { status?: string }).status === "PENDING")
    .slice(0, 4);

  const isLoading =
    myProducts.isLoading ||
    purchaseOrderStats.isLoading ||
    pendingRelease.isLoading ||
    claimStats.isLoading ||
    activeProcessingOrders.isLoading ||
    pendingProducts.isLoading ||
    topProducts.isLoading ||
    claimsLoading ||
    inboxLoading;

  if (isLoading) {
    return <SellerDashboardSkeleton />;
  }

  const avgResolution = claimStats.data?.avgResolutionDays;
  const avgResolutionLabel = avgResolution != null ? `${Math.round(avgResolution)}d` : "—";

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-8 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">
        Dashboard
      </Heading>

      {/* Row 1: operational stats */}
      <div className="grid grid-cols-2 gap-4 max-sm:gap-3 lg:grid-cols-4">
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconTruck size={24} />}
          label="Orders delivered"
          value={statValue(false, purchaseOrderStats.data?.deliveredPurchaseOrders)}
          onClick={() => navigate(`${AppRoutes.PURCHASE_ORDERS}?tab=delivered`)}
        />
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconPackage size={24} />}
          label="Pending orders"
          value={statValue(false, purchaseOrderStats.data?.processingPurchaseOrders)}
          onClick={() => navigate(`${AppRoutes.PURCHASE_ORDERS}?tab=orderPlaced`)}
        />
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconClipboardList size={24} />}
          label="Pending release"
          value={statValue(false, pendingRelease.data?.unpaidBillCount)}
          sub={
            pendingRelease.data && pendingRelease.data.totalAmount > 0
              ? dollarFormat(pendingRelease.data.totalAmount, pendingRelease.data.currency ?? "QAR")
              : undefined
          }
          onClick={() => navigate(AppRoutes.PAYMENTS)}
        />
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconBox size={24} />}
          label="Active products"
          value={statValue(false, myProducts.data?.meta?.totalCount)}
          onClick={() => navigate(AppRoutes.MY_PRODUCTS)}
        />
      </div>

      {/* Row 2: KPI stats — on phones it reads as one 2-up grid with row 1. */}
      <div className="grid grid-cols-2 gap-4 max-sm:-mt-3 max-sm:gap-3 lg:grid-cols-4">
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconClipboardCheck size={24} />}
          label="Total orders"
          value={statValue(false, purchaseOrderStats.data?.totalPurchaseOrders)}
          onClick={() => navigate(AppRoutes.PURCHASE_ORDERS)}
        />
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconTruckDelivery size={24} />}
          label="In transit"
          value={statValue(false, purchaseOrderStats.data?.inTransitPurchaseOrders)}
          onClick={() => navigate(`${AppRoutes.PURCHASE_ORDERS}?tab=inTransit`)}
        />
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconShieldCheck size={24} />}
          label="Warranty claims"
          value={statValue(false, claimStats.data?.total)}
          onClick={() => navigate(AppRoutes.WARRANTY_CLAIMS)}
        />
        <StatCard
          className={STAT_CARD_PHONE}
          icon={<IconClock size={24} />}
          label="Avg resolution"
          value={avgResolutionLabel}
          sub={avgResolution != null ? "days avg" : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SectionCard
          shadow
          className="h-full"
          title="Active Orders"
          action={
            <Button variant="underlinedGhostDark" size="sm" onClick={() => navigate(AppRoutes.PURCHASE_ORDERS)}>
              View All
            </Button>
          }
        >
          {activeOrders.length ? (
            <div className="flex flex-col gap-3">
              {activeOrders.map((order) => (
                <PreviewListRow
                  key={order.id}
                  thumbnail={<img src={PLACEHOLDER_IMG} alt="" className="size-full object-cover" />}
                  title={order.name ?? `Order #${order.id}`}
                  subtitle={`${order.orderLineIds?.length ?? 0} line items · ${formatStatusLabel(mapPoStatus(order))}`}
                  value={dollarFormat(order.amountTotal ?? 0, "QAR")}
                  actionLabel="Preview Order"
                  className={PREVIEW_ROW_PHONE}
                  onClick={() => navigate(AppRoutes.PURCHASE_ORDER_DETAILS.replace(":id", String(order.id)))}
                />
              ))}
            </div>
          ) : (
            <EmptyRow icon={IconTruck} title="No active orders right now." />
          )}
        </SectionCard>

        <SectionCard
          shadow
          className="h-full"
          title="Products pending approval"
          action={
            <Button variant="underlinedGhostDark" size="sm" onClick={() => navigate(`${AppRoutes.MY_PRODUCTS}?tab=requests`)}>
              Manage
            </Button>
          }
        >
          {pendingProductRows.length ? (
            <div className="flex flex-col gap-3">
              {pendingProductRows.map((p) => (
                <ProductRow
                  key={p.id}
                  image={(p as { image?: string | null }).image ?? PLACEHOLDER_IMG}
                  name={p.name ?? "—"}
                  sub={p.createdAt ? `Created on ${new Date(p.createdAt).toLocaleDateString()}` : ""}
                  right={<Chip variant="underReview">Under Review</Chip>}
                  onClick={() => navigate(AppRoutes.PRODUCT_REQUEST_DETAILS.replace(":id", String(p.id)))}
                />
              ))}
            </div>
          ) : (
            <EmptyRow icon={IconPackage} title="No products awaiting approval." />
          )}
        </SectionCard>

        <SectionCard
          shadow
          className="h-full"
          title="Recent Warranty Claims"
          action={
            <Button variant="underlinedGhostDark" size="sm" onClick={() => navigate(AppRoutes.WARRANTY_CLAIMS)}>
              View All
            </Button>
          }
        >
          <RecentClaimsSection
            claims={recentClaims}
            onClickClaim={(id) => navigate(AppRoutes.WARRANTY_CLAIM_DETAILS.replace(":id", id))}
          />
        </SectionCard>

        <SectionCard
          shadow
          className="h-full"
          title="Recent Notifications"
          action={
            <Button variant="underlinedGhostDark" size="sm" onClick={() => navigate(AppRoutes.INBOX)}>
              View All
            </Button>
          }
        >
          <RecentNotificationsSection
            notifications={recentNotifications}
            onClickNotification={(n) => {
              const link = resolveNotificationLink(n, { isSeller: true });
              if (link) navigate(link);
            }}
          />
        </SectionCard>
      </div>

      <SectionCard shadow title="Top products this month">
        {topProducts.data?.length ? (
          <div className="flex flex-col gap-3">
            {topProducts.data.map((p) => (
              <ProductRow
                key={p.productId}
                image={PLACEHOLDER_IMG}
                name={p.productName}
                sub={`${p.units} units`}
                right={<Chip variant="approved">{p.orders} orders</Chip>}
              />
            ))}
          </div>
        ) : (
          <EmptyRow icon={IconBox} title="No sales in the last 30 days yet." />
        )}
      </SectionCard>
    </div>
  );
}

export default SellerDashboardPage;
